import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";

export default function AdminFeatureFlags() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/feature-flags").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const toggle = async (f) => {
    setBusy(f.key);
    try {
      await api.patch(`/admin/feature-flags/${f.key}`, { key: f.key, enabled: !f.enabled });
      toast.success(`${f.label} ${!f.enabled ? "enabled" : "disabled"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="admin-flags" className="space-y-8">
      <header>
        <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Feature toggles</div>
        <h1 className="editorial-h1 text-5xl mt-2">Turn on, turn off.</h1>
        <p className="text-sm text-brand-charcoal/65 mt-3 max-w-xl">Every advanced feature is reversible. Flip the switch — the website updates instantly without redeploying.</p>
      </header>

      <div className="grid md:grid-cols-2 gap-5" data-testid="flags-list">
        {items.map((f) => (
          <div key={f.key} className="card-elevated p-6 flex items-center justify-between gap-4" data-testid={`flag-${f.key}`}>
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red flex items-center gap-1.5"><Sparkles size={12} /> {f.label || f.key}</div>
              <div className="font-display text-xl mt-1">{f.key}</div>
              {f.description && <p className="text-sm text-brand-charcoal/65 mt-2">{f.description}</p>}
            </div>
            <button onClick={() => toggle(f)} disabled={busy === f.key} data-testid={`flag-toggle-${f.key}`}
              role="switch" aria-checked={f.enabled}
              className={`relative inline-flex h-9 w-16 shrink-0 cursor-pointer rounded-full border-2 transition-colors ${f.enabled ? "bg-brand-charcoal border-brand-charcoal" : "bg-brand-muted border-brand-line"}`}>
              <span aria-hidden="true" className={`pointer-events-none inline-block h-7 w-7 rounded-full bg-white shadow-md transform ring-0 transition duration-200 ${f.enabled ? "translate-x-7" : "translate-x-0"}`} />
            </button>
          </div>
        ))}
        {items.length === 0 && <div className="text-brand-charcoal/60 py-8">Loading…</div>}
      </div>
    </div>
  );
}
